import React, { useCallback, useEffect, useRef, useState } from 'react'
import { CaretLeft, CaretRight } from '@phosphor-icons/react'
import { IconButton } from '@shopflo/ui'
import type { V2AppMedia } from 'src/lib/types/appsIntegrations'
import VideoTile from './VideoTile'

const MEDIA_WIDTH = 360
const MEDIA_GAP = 12
const SCROLL_STEP = MEDIA_WIDTH + MEDIA_GAP

const isVideoMedia = (m: V2AppMedia) => (m.type ?? '').toUpperCase() === 'VIDEO'

interface DetailMediaCarouselProps {
  items: V2AppMedia[]
  fallbackLabel: string
  playingMediaIndex: number | null
  onPlayMedia: (index: number) => void
}

const DetailMediaCarousel: React.FC<DetailMediaCarouselProps> = ({
  items,
  fallbackLabel,
  playingMediaIndex,
  onPlayMedia
}) => {
  const trackRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const syncScrollState = useCallback(() => {
    const el = trackRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 4)
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4)
  }, [])

  useEffect(() => {
    syncScrollState()
    const el = trackRef.current
    if (!el) return
    el.addEventListener('scroll', syncScrollState, { passive: true })
    const ro = new ResizeObserver(syncScrollState)
    ro.observe(el)
    return () => {
      el.removeEventListener('scroll', syncScrollState)
      ro.disconnect()
    }
  }, [syncScrollState, items.length])

  const scrollBy = (direction: 'left' | 'right') => {
    trackRef.current?.scrollBy({
      left: direction === 'left' ? -SCROLL_STEP : SCROLL_STEP,
      behavior: 'smooth'
    })
  }

  if (!items.length) return null

  return (
    <div className='relative mb-2'>
      {canScrollLeft && (
        <button
          type='button'
          onClick={() => scrollBy('left')}
          className='absolute -left-8 top-1/2 -translate-y-1/2 z-10 w-7 h-7 flex items-center justify-center transition-colors'
          aria-label='Scroll left'
        >
          <IconButton IconComponent={CaretLeft} size='sm' variant='ghost' iconProps={{ weight: 'bold' }} />
        </button>
      )}
      {canScrollRight && (
        <button
          type='button'
          onClick={() => scrollBy('right')}
          className='absolute -right-8 top-1/2 -translate-y-1/2 z-10 w-7 h-7 flex items-center justify-center transition-colors'
          aria-label='Scroll right'
        >
          <IconButton IconComponent={CaretRight} size='sm' variant='ghost' iconProps={{ weight: 'bold' }} />
        </button>
      )}

      <div
        ref={trackRef}
        className='flex gap-3 overflow-x-auto scroll-smooth snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
      >
        {items.map((media, i) => (
          <div
            key={i}
            className={`snap-start flex-shrink-0 w-[360px] h-[200px] rounded-xl overflow-hidden ${
              isVideoMedia(media) ? 'bg-zinc-900' : 'bg-zinc-200'
            }`}
          >
            {isVideoMedia(media) ? (
              <VideoTile
                media={media}
                fallbackLabel={fallbackLabel}
                isPlaying={playingMediaIndex === i}
                onPlay={() => onPlayMedia(i)}
              />
            ) : (
              <div className="relative h-full w-full rounded-[inherit] after:pointer-events-none after:absolute after:inset-0 after:rounded-[inherit] after:content-[''] after:shadow-[inset_0_0_0_1px_theme(colors.zinc.950/0.10)]">
                <img
                  src={media.url}
                  alt={media.altText ?? fallbackLabel}
                  className='w-full h-full object-cover'
                  onError={e => {
                    ;(e.target as HTMLImageElement).style.display = 'none'
                  }}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

export default DetailMediaCarousel
